import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { DataOptions } from "vuetify";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import {
  downloadStandingOrder,
  editStandingOrder,
  getContractorStandingOrderItems,
  getContractorStandingOrders,
  removeStandingOrder,
} from "@/requests/administration/contractors/contractorStandingOrders";
import { FILTER_NAMES } from "@/static/filterNames";
import _ from "lodash";
import {
  StandingOrder,
  StandingOrderItemTableResponse,
  StandingOrderTableResponse,
} from "@/models/api/administration/contractors/ContractorStandingOrder";
import { IDialog } from "@/models/components/Dialog";
import { ConfirmModel } from "@/models/components/popup/Confirm";
import { filterCapitalize } from "@/filters/capitalize";
import i18n from "@/plugins/i18n";

export interface AdministrationContractorStandingOrderInterface {
  standingOrderFilters: null | Array<CriterionBase>;
  standingOrderDataTableOptions: DataOptions;
  standingOrderListResult: StandingOrderTableResponse;
  selectedStandingOrderId: null | string;
  // Items
  standingOrderItemFilters: null | Array<CriterionBase>;
  standingOrderItemDataTableOptions: DataOptions;
  standingOrderItemListResult: StandingOrderItemTableResponse;
  selectedStandingOrderItemId: null | string;
  // Dialog
  dialog: IDialog;
  // RecordStamp
  recordStampId: null | number;
}

export const AdministrationContractorStandingOrderState: AdministrationContractorStandingOrderInterface = {
  standingOrderFilters: null,
  standingOrderDataTableOptions: new DefaultDataOptions(),
  standingOrderListResult: new StandingOrderTableResponse(),
  selectedStandingOrderId: null,
  // Items
  standingOrderItemFilters: null,
  standingOrderItemDataTableOptions: new DefaultDataOptions(),
  standingOrderItemListResult: new StandingOrderItemTableResponse(),
  selectedStandingOrderItemId: null,
  // Dialog
  dialog: {
    isVisible: false,
    mode: "add",
  },
  // RecordStamp
  recordStampId: null,
};

const AdministrationContractorStandingOrderStateModule = defineModule({
  state: AdministrationContractorStandingOrderState as AdministrationContractorStandingOrderInterface,
  namespaced: true as const,
  getters: {
    selectedStandingOrder(...args): StandingOrder | undefined {
      const { state } = _moduleGetterContext(args);
      return state.standingOrderListResult.items.find((x) => x.id == state.selectedStandingOrderId);
    },
  },
  mutations: {
    setStandingOrderFilters(state, param: Array<CriterionBase>) {
      state.standingOrderFilters = param;
    },
    setStandingOrderDataTableOptions(state, param: DataOptions) {
      state.standingOrderDataTableOptions = param;
    },
    setStandingOrderResponse(state, param: StandingOrderTableResponse) {
      state.standingOrderListResult = param;
    },
    setSelectedStandingOrderId(state, param: string | null) {
      state.selectedStandingOrderId = param;
    },
    // Items
    setStandingOrderItemFilters(state, param: Array<CriterionBase>) {
      state.standingOrderFilters = param;
    },
    setStandingOrderItemDataTableOptions(state, param: DataOptions) {
      state.standingOrderItemDataTableOptions = param;
    },
    setStandingOrderItemResponse(state, param: StandingOrderItemTableResponse) {
      state.standingOrderItemListResult = param;
    },
    // Dialog
    setDialogVisibility(state, param: boolean) {
      state.dialog.isVisible = param;
    },
    setDialogMode(state, param: "add" | "edit") {
      state.dialog.mode = param;
    },
    // RecordStamp
    setRecordStampId(state, param: number | null) {
      state.recordStampId = param;
    },
  },
  actions: {
    async downloadStandingOrders(context) {
      const { commit, state, rootState, dispatch } = _moduleActionContext(context);

      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const result = await getContractorStandingOrders(
          {
            count: state.standingOrderDataTableOptions.itemsPerPage,
            page: state.standingOrderDataTableOptions.page - 1,
            criterions: _.cloneDeep(state.standingOrderFilters),
            filterName: FILTER_NAMES.StandingOrdersFilter,
          },
          rootState.administration.contractors.list.selectedContractorId
        );
        commit.setStandingOrderResponse(result);
      }
      await dispatch.downloadStandingOrderItems();
    },
    async downloadStandingOrderItems(context) {
      const { commit, state, rootState } = _moduleActionContext(context);

      if (state.selectedStandingOrderId !== null && rootState.administration.contractors.list.selectedContractorId) {
        const result = await getContractorStandingOrderItems(
          {
            count: state.standingOrderItemDataTableOptions.itemsPerPage,
            page: state.standingOrderItemDataTableOptions.page - 1,
            criterions: _.cloneDeep(state.standingOrderFilters),
            filterName: FILTER_NAMES.StandingOrderItemsFilter,
          },
          rootState.administration.contractors.list.selectedContractorId,
          state.selectedStandingOrderId
        );
        commit.setStandingOrderItemResponse(result);
      }
    },
    async downloadSelectedStandingOrder(context): Promise<StandingOrder> {
      const { state } = _moduleActionContext(context);
      return await downloadStandingOrder(state.selectedStandingOrderId as string);
    },
    async editStandingOrder(context, param: StandingOrder) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedStandingOrderId) {
        await editStandingOrder(state.selectedStandingOrderId, param);
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.downloadStandingOrders();
      }
    },
    async removeStandingOrder(context, id: string) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      const confirm = ConfirmModel.confirmBoolFromContent(
        filterCapitalize(i18n.t("areYouSureYouWantDeleteStandingOrder"))
      );
      const isConfirmed = await rootDispatch.popup.addConfirm(confirm);
      if (isConfirmed) {
        await removeStandingOrder(id);
        await dispatch.downloadStandingOrders();
      }
    },
  },
});

export default AdministrationContractorStandingOrderStateModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorStandingOrderStateModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(
    context,
    AdministrationContractorStandingOrderStateModule
  );
